import React, { useState, useEffect } from 'react';
import { FirePreventionInstructionItem, Employee, FirePreventionInstructionItemList, FirePreventionInstructionType } from '../types/models';
import { FirePreventionInstructionItemService } from '../services/firePreventionInstructionItemService';
import { EmployeeService } from '../services/employeeService';
import { FirePreventionInstructionItemForm } from '../components/forms/FirePreventionInstructionItemForm';
import { useCompany } from '../contexts/CompanyContext';
import { API_BASE_URL } from '../services/api';

export const FirePreventionInstructionItemsPage: React.FC = () => {
  const { selectedCompany } = useCompany();
  const [instructions, setInstructions] = useState<FirePreventionInstructionItemList[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<FirePreventionInstructionItem | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [createdInstruction, setCreatedInstruction] = useState<FirePreventionInstructionItem | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      loadInstructions();
    }
  }, [selectedEmployeeId, employees]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Загружаем сотрудников
      const employeesResponse = await EmployeeService.getAll();

      if (employeesResponse.success) {
        setEmployees(employeesResponse.data);
      }

      // Загружаем инструктажи
      await loadInstructions();
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadInstructions = async () => {
    try {
      let response;

      if (selectedEmployeeId) {
        response = await FirePreventionInstructionItemService.getByEmployee(selectedEmployeeId);
      } else {
        // По умолчанию получаем все инструктажи
        response = await FirePreventionInstructionItemService.getAll(selectedCompany?.id);
      }

      if (response.success) {
        setInstructions(response.data);
        setError(null);
      } else {
        setError(response.message || 'Ошибка загрузки инструктажей');
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    }
  };

  const handleCreate = () => {
    setEditingInstruction(null);
    setShowForm(true);
  };

  const handleEdit = (instruction: FirePreventionInstructionItemList) => {
    // Convert to full FirePreventionInstructionItem for editing
    const fullInstruction: FirePreventionInstructionItem = {
      ...instruction,
      employee: employees.find(e => e.id === instruction.employeeId),
      responsibleEmployee: employees.find(e => e.id === instruction.responsibleEmployeeId),
      type: instruction.type,
    };
    setEditingInstruction(fullInstruction);
    setShowForm(true);
  };

  const handleDelete = async (instruction: FirePreventionInstructionItemList) => {
    const employeeName = getEmployeeName(instruction);

    if (window.confirm(`Вы уверены, что хотите удалить инструктаж для "${employeeName}"?`)) {
      try {
        const response = await FirePreventionInstructionItemService.delete(instruction.id);
        if (response.success) {
          loadInstructions(); // Перезагрузить список
        } else {
          setError(response.message || 'Ошибка удаления');
        }
      } catch (err) {
        setError('Ошибка удаления инструктажа');
      }
    }
  };

  const handleFormSuccess = (createdItem?: FirePreventionInstructionItem) => {
    setShowForm(false);
    setEditingInstruction(null);

    // Если это создание нового инструктажа, показать диалог экспорта
    if (createdItem && !editingInstruction) {
      setCreatedInstruction(createdItem);
      setShowExportDialog(true);
    } else {
      // Для редактирования просто перезагрузить список
      loadInstructions();
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingInstruction(null);
  };

  const handleExportDialogYes = async () => {
    if (createdInstruction) {
      await handleExport(createdInstruction.id);
    }
    setShowExportDialog(false);
    setCreatedInstruction(null);
    loadInstructions(); // Перезагрузить список после экспорта
  };

  const handleExportDialogNo = () => {
    setShowExportDialog(false);
    setCreatedInstruction(null);
    loadInstructions(); // Перезагрузить список
  };

  const handleExport = async (instructionId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/FirePreventionInstructionItem/DownloadFile?id=${instructionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Получаем blob из ответа
        const blob = await response.blob();

        // Создаем URL для blob
        const url = window.URL.createObjectURL(blob);

        // Создаем временную ссылку для скачивания
        const link = document.createElement('a');
        link.href = url;

        // Получаем имя файла из заголовка Content-Disposition или используем дефолтное
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Журнал_регистрации_противопожарного_инструктажа.docx';

        if (contentDisposition) {
          // Сначала проверяем filename*=UTF-8'' формат (новый стандарт)
          const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/);
          if (filenameStarMatch && filenameStarMatch[1]) {
            filename = decodeURIComponent(filenameStarMatch[1]);
          } else {
            // Fallback на старый формат filename=
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
              // Декодируем URL-кодированные символы
              filename = decodeURIComponent(filename);
            }
          }
        }

        link.download = filename;

        // Добавляем ссылку в DOM, кликаем и удаляем
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Освобождаем URL
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Ошибка при скачивании файла:', response.statusText);
        alert('Ошибка при скачивании файла');
      }
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      alert('Ошибка при скачивании файла');
    }
  };

  const handleDownloadAll = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/FirePreventionInstructionItem/DownloadAllFile?companyId=${selectedCompany?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Все_журналы_противопожарного_инструктажа.docx';

        if (contentDisposition) {
          const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/);
          if (filenameStarMatch && filenameStarMatch[1]) {
            filename = decodeURIComponent(filenameStarMatch[1]);
          } else {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '');
              filename = decodeURIComponent(filename);
            }
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Ошибка при скачивании файла:', response.statusText);
        alert('Ошибка при скачивании файла');
      }
    } catch (error) {
      console.error('Ошибка при скачивании файла:', error);
      alert('Ошибка при скачивании файла');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const getEmployeeName = (instruction: FirePreventionInstructionItemList) => {
    const employee = employees.find(e => e.id === instruction.employeeId);
    return employee
      ? `${employee.surname} ${employee.name}`
      : `Сотрудник ${instruction.employeeId}`;
  };

  const getResponsibleEmployeeName = (instruction: FirePreventionInstructionItemList) => {
    const employee = employees.find(e => e.id === instruction.responsibleEmployeeId);
    return employee
      ? `${employee.surname} ${employee.name}`
      : `Сотрудник ${instruction.responsibleEmployeeId}`;
  };

  const getInstructionTypeName = (type: FirePreventionInstructionType) => {
    switch (type) {
      case FirePreventionInstructionType.Init:
        return 'Вводный, Первичный';
      case FirePreventionInstructionType.First:
        return 'Первичный';
      case FirePreventionInstructionType.Repeat:
        return 'Повторный';
      default:
        return 'Неизвестный';
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Противопожарный инструктаж</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            className="form-input"
            style={{ width: '200px' }}
            value={selectedEmployeeId || ''}
            onChange={(e) => {
              setSelectedEmployeeId(Number(e.target.value) || null);
            }}
          >
            <option value="">Все сотрудники</option>
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {`${employee.surname} ${employee.name} ${employee.patronymic}`.trim()}
              </option>
            ))}
          </select>

          {selectedEmployeeId && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSelectedEmployeeId(null);
              }}
            >
              Сбросить фильтр
            </button>
          )}

          <button className="btn btn-primary" onClick={handleCreate}>
            Добавить инструктаж
          </button>

          <button className="btn btn-secondary" onClick={handleDownloadAll}>
            Выгрузить все
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Дата проведения</th>
              <th>Вид инструктажа</th>
              <th>Сотрудник</th>
              <th>Ответственный</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {instructions.map((instruction) => (
              <tr key={instruction.id}>
                <td>{formatDate(instruction.date)}</td>
                <td>{getInstructionTypeName(instruction.type)}</td>
                <td>{getEmployeeName(instruction)}</td>
                <td>{getResponsibleEmployeeName(instruction)}</td>
                <td>
                  <button
                    className="btn btn-primary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleExport(instruction.id)}
                  >
                    Экспорт
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ marginRight: '5px' }}
                    onClick={() => handleEdit(instruction)}
                  >
                    Редактировать
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(instruction)}
                  >
                    Удалить
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {instructions.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            {selectedEmployeeId
              ? 'Нет инструктажей для выбранного сотрудника.'
              : 'В базе данных пока нет противопожарных инструктажей. Создайте первый инструктаж, нажав кнопку "Добавить инструктаж".'
            }
          </div>
        )}
      </div>

      {showForm && (
        <FirePreventionInstructionItemForm
          instruction={editingInstruction || undefined}
          employeeId={selectedEmployeeId || undefined}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      {showExportDialog && (
        <div className="modal-overlay" style={{ zIndex: 1100 }}>
          <div
            className="modal-content"
            style={{ maxWidth: '400px', textAlign: 'center' }}
          >
            <h3>Экспортировать в Word?</h3>
            <p>Инструктаж успешно создан. Хотите экспортировать его в документ Word?</p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={handleExportDialogYes}
              >
                Да, экспортировать
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleExportDialogNo}
              >
                Нет, спасибо
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};